import React, { useEffect, useRef, useId } from "react";
import { jobStateSpec, SOURCE_LABELS } from "../../lib/printing/printingModel";

// Small accessible building blocks for the printing dashboard. Native
// controls with Tailwind classes (consistent with the rest of the
// dashboard) so they are keyboard-friendly, testable in jsdom and usable
// with a finger on a Samsung tablet (44 px targets).

export const inputCls =
  "h-11 w-full rounded-md border border-stone-300 bg-white px-3 text-sm text-stone-900 " +
  "focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-1 disabled:bg-stone-100 disabled:text-stone-500";
export const btnBase =
  "inline-flex items-center justify-center gap-2 min-h-[44px] rounded-md px-4 text-sm font-semibold " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
export const btnPrimary = `${btnBase} bg-emerald-700 text-white hover:bg-emerald-800`;
export const btnSecondary = `${btnBase} border border-stone-300 bg-white text-stone-800 hover:bg-stone-50`;
export const btnDanger = `${btnBase} bg-red-700 text-white hover:bg-red-800`;
export const btnLink = "inline-flex items-center min-h-[44px] px-2 text-sm font-medium text-emerald-800 underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 rounded";

export function Field({ label, hint, error, children, required, id }) {
  const autoId = useId();
  const fieldId = id || autoId;
  const child = React.Children.only(children);
  const describedBy = [hint ? `${fieldId}-hint` : null, error ? `${fieldId}-error` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className="space-y-1">
      <label htmlFor={fieldId} className="block text-sm font-medium text-stone-800">
        {label}{required && <span aria-hidden="true" className="text-red-700"> *</span>}
      </label>
      {React.cloneElement(child, {
        id: fieldId,
        "aria-invalid": error ? "true" : undefined,
        "aria-describedby": describedBy,
        "aria-required": required ? "true" : undefined,
      })}
      {hint && <p id={`${fieldId}-hint`} className="text-xs text-stone-600">{hint}</p>}
      {error && <p id={`${fieldId}-error`} role="alert" className="text-xs font-medium text-red-700">{error}</p>}
    </div>
  );
}

export function CheckField({ label, hint, checked, onChange, disabled, id, testId }) {
  const autoId = useId();
  const fieldId = id || autoId;
  return (
    <div className="flex items-start gap-3 py-1">
      <input
        id={fieldId} type="checkbox" checked={Boolean(checked)} disabled={disabled} data-testid={testId}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 rounded border-stone-400 text-emerald-700 focus:ring-2 focus:ring-emerald-600"
      />
      <label htmlFor={fieldId} className="text-sm text-stone-800 min-h-[28px] cursor-pointer">
        <span className="font-medium">{label}</span>
        {hint && <span className="block text-xs text-stone-600">{hint}</span>}
      </label>
    </div>
  );
}

const TONES = {
  neutral: "bg-stone-100 text-stone-800 border-stone-300",
  info: "bg-sky-50 text-sky-900 border-sky-300",
  success: "bg-emerald-50 text-emerald-900 border-emerald-300",
  warning: "bg-amber-50 text-amber-900 border-amber-300",
  danger: "bg-red-50 text-red-900 border-red-300",
};

export function StateBadge({ state }) {
  const spec = jobStateSpec(state);
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ${TONES[spec.tone]}`}
      data-testid={`state-badge-${state}`} data-state={state}>
      <span aria-hidden="true">{spec.glyph}</span>{" "}
      <span>{spec.label}</span>
    </span>
  );
}

export function Pill({ tone = "neutral", children, testId }) {
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${TONES[tone]}`} data-testid={testId}>
      {children}
    </span>
  );
}

export function SourceTag({ source }) {
  const tone = source === "catalog" ? "info" : source === "tenant" ? "success" : "neutral";
  return <Pill tone={tone} testId={`source-${source}`}>{SOURCE_LABELS[source] || source}</Pill>;
}

export function Notice({ tone = "info", title, children, testId, role }) {
  const r = role || (tone === "danger" || tone === "warning" ? "alert" : "status");
  return (
    <div role={r} className={`rounded-md border p-3 text-sm ${TONES[tone]}`} data-testid={testId}>
      {title && <strong className="block">{title}</strong>}
      {children}
    </div>
  );
}

export function EmptyState({ children, testId }) {
  return <p className="rounded-md border border-dashed border-stone-300 p-4 text-sm text-stone-600" data-testid={testId}>{children}</p>;
}

export function Loading({ label = "Loading…" }) {
  return <p role="status" aria-live="polite" className="text-sm text-stone-600" data-testid="printing-loading">{label}</p>;
}

export function KeyValue({ items }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
      {items.filter(Boolean).map(([k, v]) => (
        <div key={k} className="flex flex-col sm:flex-row sm:gap-2">
          <dt className="text-stone-600 sm:w-44 shrink-0">{k}</dt>
          <dd className="font-medium text-stone-900 break-words">{v ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SectionCard({ title, description, actions, children, testId }) {
  return (
    <section className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm space-y-3" data-testid={testId} aria-label={title}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-stone-900">{title}</h2>
          {description && <p className="text-sm text-stone-600">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/**
 * Accessible modal: role=dialog, aria-modal, labelled by its title, focus
 * moved inside on open, Tab trapped, Escape closes, focus restored on
 * close. No portal so it renders predictably in jsdom.
 */
export function Modal({ open, title, description, onClose, children, testId, wide }) {
  const ref = useRef(null);
  const titleId = useId();
  const descId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const node = ref.current;
    const focusables = () => Array.from(node?.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) || []);
    const first = focusables()[0];
    (first || node)?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); return; }
      if (e.key === "Tab") {
        const items = focusables();
        if (items.length === 0) return;
        const firstEl = items[0]; const lastEl = items[items.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
        else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
      }
    };
    node?.addEventListener("keydown", onKey);
    return () => { node?.removeEventListener("keydown", onKey); if (previous && previous.focus) previous.focus(); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4">
      <div
        ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descId : undefined}
        tabIndex={-1} data-testid={testId}
        className={`max-h-[95vh] w-full overflow-y-auto rounded-t-xl bg-white p-5 shadow-xl sm:rounded-xl ${wide ? "sm:max-w-3xl" : "sm:max-w-xl"}`}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 id={titleId} className="text-lg font-semibold text-stone-900">{title}</h3>
            {description && <p id={descId} className="text-sm text-stone-600">{description}</p>}
          </div>
          <button type="button" onClick={onClose} className={btnSecondary} aria-label="Close dialog">Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Tab bar with roving focus (Arrow keys, Home, End) and aria-selected. */
export function TabBar({ tabs, active, onChange }) {
  const refs = useRef({});
  const onKey = (e, idx) => {
    const n = tabs.length;
    let next = null;
    if (e.key === "ArrowRight") next = (idx + 1) % n;
    if (e.key === "ArrowLeft") next = (idx - 1 + n) % n;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = n - 1;
    if (next !== null) { e.preventDefault(); onChange(tabs[next].id); refs.current[tabs[next].id]?.focus(); }
  };
  return (
    <div role="tablist" aria-label="Printing sections" className="flex gap-1 overflow-x-auto border-b border-stone-200 pb-px">
      {tabs.map((t, idx) => {
        const selected = t.id === active;
        return (
          <button
            key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={selected} aria-controls={`panel-${t.id}`}
            tabIndex={selected ? 0 : -1} ref={(el) => { refs.current[t.id] = el; }}
            onClick={() => onChange(t.id)} onKeyDown={(e) => onKey(e, idx)} data-testid={`tab-${t.id}`}
            className={`min-h-[44px] whitespace-nowrap rounded-t-md px-3 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 ${
              selected ? "border-b-2 border-emerald-700 text-emerald-900" : "text-stone-600 hover:text-stone-900"
            }`}
          >
            {t.label}
            {t.badge != null && t.badge !== 0 && (
              <span className="ml-2 rounded-full bg-red-700 px-2 text-xs font-semibold text-white" aria-label={`${t.badge} needing attention`}>{t.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ id, active, children }) {
  if (id !== active) return null;
  return <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} className="space-y-4 pt-4">{children}</div>;
}
